<script lang="ts">
  import Logo from './Logo.svelte'
  import Icon from './Icon.svelte'
  import Button from './Button.svelte'
  import { theme } from '../theme.svelte'
  import type { Router } from '../router.svelte'

  interface Props { router: Router }
  let { router }: Props = $props()

  // Backends and Security used to be reachable only from the footer — the
  // first is the actual product catalogue (38 pages), the second is the
  // page a buyer evaluating whether to trust this with their credentials
  // most wants to find without hunting under "Legal (EN)". Both are added
  // rather than swapped in for an existing link: nothing here was wrong,
  // only incomplete.
  const LINKS = [
    { href: '/features', label: 'Features' },
    { href: '/backends', label: 'Backends' },
    { href: '/protocols', label: 'Protocols' },
    { href: '/pricing', label: 'Pricing' },
    { href: '/download', label: 'Download' },
    { href: '/docs', label: 'Docs' },
    { href: '/security', label: 'Security' },
    { href: '/support', label: 'Support' },
    { href: '/news', label: 'News' },
  ]

  let open = $state(false)
  let scrolled = $state(false)

  // Close the drawer on navigation. Leaving it open over the new page is the
  // classic SPA mobile-menu bug.
  $effect(() => {
    router.path
    open = false
  })

  // Lock the page behind the drawer so the body does not scroll under it.
  $effect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  })

  $effect(() => {
    const onScroll = () => { scrolled = window.scrollY > 8 }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  })

  function isActive(href: string): boolean {
    return router.path === href || router.path.startsWith(href + '/')
  }
</script>

<svelte:window
  onkeydown={(e) => { if (e.key === 'Escape' && open) open = false }}
/>

<a class="skip" href="#main">Skip to content</a>

<header class="nav" class:nav--scrolled={scrolled}>
  <div class="page page--wide nav__inner">
    <a class="nav__brand" href="/" aria-label="Mountit home"><Logo size={26} /></a>

    <nav class="nav__links" aria-label="Primary">
      {#each LINKS as link (link.href)}
        <a
          href={link.href}
          class="nav__link"
          class:is-active={isActive(link.href)}
          aria-current={isActive(link.href) ? 'page' : undefined}
        >{link.label}</a>
      {/each}
    </nav>

    <div class="nav__actions">
      <button
        class="icon-btn"
        onclick={() => theme.cycle()}
        aria-label="Switch to {theme.resolved === 'dark' ? 'light' : 'dark'} theme"
        title="Switch theme"
      >
        <Icon name={theme.resolved === 'dark' ? 'sun' : 'moon'} size={17} />
      </button>

      <!-- No sign-in and no buy button: the shop is not open, and a button
           that leads to an apology is worse than no button. -->
      <Button href="/download" size="sm">Get Mountit</Button>

      <button
        class="icon-btn nav__burger"
        onclick={() => (open = !open)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        aria-controls="nav-drawer"
      >
        <Icon name={open ? 'x' : 'menu'} size={20} />
      </button>
    </div>
  </div>
</header>

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
  <div class="scrim" onclick={() => (open = false)}></div>
  <div class="drawer" id="nav-drawer">
    <nav class="drawer__links" aria-label="Mobile">
      {#each LINKS as link (link.href)}
        <a href={link.href} class="drawer__link" class:is-active={isActive(link.href)}>{link.label}</a>
      {/each}
      <hr />
      <div class="drawer__cta"><Button href="/download" block size="lg">Get Mountit</Button></div>
    </nav>
  </div>
{/if}

<style>
  .skip {
    position: absolute;
    left: 50%;
    transform: translate(-50%, -140%);
    z-index: 200;
    padding: 0.6rem 1.1rem;
    background: var(--accent);
    color: #fff;
    border-radius: 0 0 var(--radius) var(--radius);
    transition: transform 0.15s ease;
  }
  .skip:focus { transform: translate(-50%, 0); }

  .nav {
    position: sticky;
    top: 0;
    z-index: 100;
    height: var(--nav-h);
    display: flex;
    align-items: center;
    background: color-mix(in srgb, var(--bg) 82%, transparent);
    backdrop-filter: saturate(180%) blur(14px);
    -webkit-backdrop-filter: saturate(180%) blur(14px);
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease, background-color 0.2s ease;
  }
  .nav--scrolled { border-bottom-color: var(--border); }

  .nav__inner { display: flex; align-items: center; gap: 1.5rem; width: 100%; }
  .nav__brand { flex: none; }
  .nav__brand:hover { text-decoration: none; }

  .nav__links { display: flex; align-items: center; gap: 0.25rem; margin-right: auto; }

  .nav__link {
    padding: 0.4rem 0.66rem;
    border-radius: var(--radius-sm);
    font-size: 0.895rem;
    font-weight: 500;
    color: var(--text-muted);
    transition: color 0.14s ease, background-color 0.14s ease;
  }
  .nav__link:hover { color: var(--text); background: var(--surface-hover); text-decoration: none; }
  .nav__link.is-active { color: var(--text); font-weight: 570; }

  .nav__actions { display: flex; align-items: center; gap: 0.55rem; margin-left: auto; }

  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px; height: 32px;
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
    transition: background-color 0.14s ease, color 0.14s ease;
  }
  .icon-btn:hover { background: var(--surface-hover); color: var(--text); }

  .nav__burger { display: none; }

  .scrim {
    position: fixed;
    inset: var(--nav-h) 0 0;
    z-index: 90;
    background: color-mix(in srgb, var(--bg-sunken) 72%, transparent);
    backdrop-filter: blur(3px);
  }

  .drawer {
    position: fixed;
    inset: var(--nav-h) 0 auto;
    z-index: 95;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
    box-shadow: var(--shadow-lg);
    max-height: calc(100dvh - var(--nav-h));
    overflow-y: auto;
  }
  .drawer__links { display: flex; flex-direction: column; padding: 0.75rem var(--gutter) 1.5rem; }
  .drawer__link {
    padding: 0.8rem 0.25rem;
    font-size: 1.03rem;
    font-weight: 540;
    color: var(--text);
    border-bottom: 1px solid var(--border);
  }
  .drawer__link:hover { text-decoration: none; color: var(--accent-text); }
  .drawer__link.is-active { color: var(--accent-text); }
  .drawer__cta { margin-top: 1.25rem; }
  .drawer hr { margin: 1rem 0 0; }

  @media (max-width: 900px) {
    .nav__links { display: none; }
    .nav__burger { display: inline-flex; }
    .nav__link--quiet { display: none; }
  }
  @media (max-width: 520px) {
    .nav__actions :global(.btn) { display: none; }
    .nav__actions .icon-btn { display: inline-flex; }
  }
</style>
